// The ONE invalidation/patch matrix for email surfaces. Every optimistic update flows
// through here. Two server pipelines feed two caches — ["inbox"] (/inbox + pop-out) and
// ["account-emails", id] (+ the ["email-tasks", id] / ["pinned-emails-map", id] live
// overlays) for the AccountDetail tabs — so an action must reach BOTH for the row's
// client (accountIdForAction), or the account tab shows stale state after an /inbox
// action (v1.5.71 seam fix). Scope limit: this syncs caches within ONE browser window;
// the pop-out is a separate window with its own QueryClient and converges only via its
// 60s poll / refetch-on-mount. Pure transforms are exported for tests; thin QueryClient
// wrappers apply them to the live caches.

import type { QueryClient } from "@tanstack/react-query";
import type { ClientEmail, InboxGroup, InboxMessage } from "@/lib/api";

/** Overlay/state fields an action may patch onto a message (unified field names). */
export interface EmailPatch {
  isRead?: boolean;
  flagStatus?: string;
  categories?: string[];
  taskStatus?: string;
  assignedToUserId?: string | null;
  assignedToName?: string | null;
  taskNotes?: string | null;
  pinned?: boolean;
  pinnedProjectId?: string;
  pinnedProjectTitle?: string;
}

function patchInboxMsg(m: InboxMessage, patch: EmailPatch): InboxMessage {
  const next: InboxMessage = { ...m };
  if (patch.isRead !== undefined) next.isRead = patch.isRead;
  if (patch.flagStatus !== undefined) next.flagStatus = patch.flagStatus;
  if (patch.categories !== undefined) next.categories = patch.categories;
  if (patch.taskStatus !== undefined) next.taskStatus = patch.taskStatus;
  if (patch.assignedToUserId !== undefined) next.assignedToUserId = patch.assignedToUserId ?? undefined;
  if (patch.assignedToName !== undefined) next.assignedToName = patch.assignedToName ?? undefined;
  if (patch.taskNotes !== undefined) next.taskNotes = patch.taskNotes ?? undefined;
  if (patch.pinned !== undefined) next.pinned = patch.pinned;
  if (patch.pinnedProjectId !== undefined) next.pinnedProjectId = patch.pinnedProjectId;
  if (patch.pinnedProjectTitle !== undefined) next.pinnedProjectTitle = patch.pinnedProjectTitle;
  return next;
}

/** Pure: patch one message in the /inbox groups payload, keeping group unread honest. */
export function applyPatchToInboxGroups(
  groups: InboxGroup[],
  mailbox: string,
  messageId: string,
  patch: EmailPatch,
): InboxGroup[] {
  return groups.map((g) => {
    if (g.mailbox_address !== mailbox) return g;
    let unreadDelta = 0;
    const patchList = (list: InboxMessage[] | undefined, countsTowardUnread: boolean) =>
      list?.map((m) => {
        if (m.id !== messageId) return m;
        if (countsTowardUnread && patch.isRead !== undefined && patch.isRead !== m.isRead) {
          unreadDelta += patch.isRead ? -1 : 1;
        }
        return patchInboxMsg(m, patch);
      });
    const messages = patchList(g.messages, true) ?? g.messages;
    // Filtered rows are already excluded from the de-noised group unread — no delta.
    const filtered = patchList(g.filtered, false);
    return {
      ...g,
      messages,
      ...(filtered !== undefined ? { filtered } : {}),
      unread: Math.max(0, g.unread + unreadDelta),
    };
  });
}

export interface AccountEmailsData {
  emails: ClientEmail[];
  filtered?: ClientEmail[];
  sharedUnread?: number;
  makershubForwardConfigured?: boolean;
  warning?: string;
}

function patchClientEmail(e: ClientEmail, patch: EmailPatch): ClientEmail {
  const next: ClientEmail = { ...e };
  if (patch.isRead !== undefined) next.isRead = patch.isRead;
  if (patch.flagStatus !== undefined) next.flag = { flagStatus: patch.flagStatus as any };
  if (patch.categories !== undefined) next.categories = patch.categories;
  if (patch.taskStatus !== undefined) next.taskStatus = patch.taskStatus;
  if (patch.assignedToUserId !== undefined) next.assignedToUserId = patch.assignedToUserId ?? undefined;
  if (patch.assignedToName !== undefined) next.assignedToName = patch.assignedToName ?? undefined;
  if (patch.taskNotes !== undefined) next.taskNotes = patch.taskNotes ?? undefined;
  if (patch.pinned !== undefined) next.pinned = patch.pinned;
  if (patch.pinnedProjectId !== undefined) next.pinnedProjectId = patch.pinnedProjectId;
  if (patch.pinnedProjectTitle !== undefined) next.pinnedProjectTitle = patch.pinnedProjectTitle;
  return next;
}

/** Pure: patch one row in the account-emails payload, keeping sharedUnread honest. */
export function applyPatchToAccountEmails(
  data: AccountEmailsData,
  messageId: string,
  patch: EmailPatch,
): AccountEmailsData {
  let sharedUnreadDelta = 0;
  const patchList = (list: ClientEmail[] | undefined, countsTowardUnread: boolean) =>
    list?.map((e) => {
      if (e.id !== messageId) return e;
      if (
        countsTowardUnread &&
        e.isSharedMailbox &&
        patch.isRead !== undefined &&
        patch.isRead !== e.isRead
      ) {
        sharedUnreadDelta += patch.isRead ? -1 : 1;
      }
      return patchClientEmail(e, patch);
    });
  const emails = patchList(data.emails, true) ?? data.emails;
  const filtered = patchList(data.filtered, false);
  return {
    ...data,
    emails,
    ...(filtered !== undefined ? { filtered } : {}),
    ...(data.sharedUnread !== undefined
      ? { sharedUnread: Math.max(0, data.sharedUnread + sharedUnreadDelta) }
      : {}),
  };
}

/** Which account's caches an action on this row must reach. The row's own account wins
 * (inbox rows carry group.account_id); an account-scoped surface is the fallback; a My Mail
 * row (accountId "") on the inbox surface reaches no account cache. */
export function accountIdForAction(
  scope: { surface: "inbox" } | { surface: "account"; accountId: string },
  email: { accountId?: string | null },
): string | undefined {
  if (email.accountId) return email.accountId;
  return scope.surface === "account" ? scope.accountId : undefined;
}

// ── QueryClient wrappers ──────────────────────────────────────────────────────

export function patchInboxMessage(qc: QueryClient, mailbox: string, messageId: string, patch: EmailPatch): void {
  qc.setQueryData<InboxGroup[]>(["inbox"], (prev) =>
    prev ? applyPatchToInboxGroups(prev, mailbox, messageId, patch) : prev,
  );
}

export function patchAccountEmail(qc: QueryClient, accountId: string, messageId: string, patch: EmailPatch): void {
  qc.setQueryData<AccountEmailsData>(["account-emails", accountId], (prev) =>
    prev ? applyPatchToAccountEmails(prev, messageId, patch) : prev,
  );
}

/** Mark the server-truth caches stale after a mutation lands. `accountId` adds the
 * account-page keys. Note: ["account-emails"] is actively observed while an email tab
 * is open (Phase E auto-load), so invalidating it there triggers ONE refetch of the
 * account endpoint per action — bounded, deliberate: it re-syncs overlays with server
 * truth after every claim/pin/flag. */
export function invalidateEmailSurfaces(
  qc: QueryClient,
  opts: { accountId?: string; waitingOn?: boolean; projectId?: string } = {},
): void {
  qc.invalidateQueries({ queryKey: ["inbox"] });
  if (opts.accountId) {
    qc.invalidateQueries({ queryKey: ["account-emails", opts.accountId] });
    qc.invalidateQueries({ queryKey: ["email-tasks", opts.accountId] });
    qc.invalidateQueries({ queryKey: ["pinned-emails-map", opts.accountId] });
  }
  if (opts.waitingOn) qc.invalidateQueries({ queryKey: ["waiting-on"] });
  if (opts.projectId) qc.invalidateQueries({ queryKey: ["project-pinned-emails", opts.projectId] });
}
